import React, { useEffect } from 'react'
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import AuthFaberApi from 'api/faber/AuthAPI'
import OrbitSession from 'hooks/useCookies'
import useIdentity from 'hooks/useIdentity'
import {
  AccountManagement,
  AddAccount,
  ESimsManagementPage,
  FaberCustomerManagement,
  FaberFinanceMainDashboard,
  FaberGlobalDashboard,
  FaberOtpDashboard,
  FaberRewardsDashboard,
  FaberTerminatedCustomersMainDashboard,
  FaberViewProfilePage,
  FaberViewProfilePageV2,
  LogIn,
  NotFound,
  PermissionedRoute,
  PreorderUpload,
  Profile,
  UploadESims,
  ViewAndUpdateAccount
} from 'pages'
import APIOutAccessManagement from 'pages/B2B/APIOutAccessManagement'
import AddAPIKey from 'pages/B2B/APIOutAccessManagement/components/AddAPIKey'
import SearchAPIKey from 'pages/B2B/APIOutAccessManagement/components/SearchAPIKey'
import CatalogManagement from 'pages/B2B/CatalogManagement'
import AddCatalogGroup from 'pages/B2B/CatalogManagement/components/AddCatalogGroup'
import SearchCatalogGroup from 'pages/B2B/CatalogManagement/components/SearchCatalogGroup'
import CustomerSearch from 'pages/B2B/CustomerStatement'
import CustomerStatementSearchForm from 'pages/B2B/CustomerStatement/components/CustomerStatement'
import OrderManagement from 'pages/B2B/OrderSearch'
import OrderSearchForm from 'pages/B2B/OrderSearch/components/OrderSearch'
import PaymentCollection from 'pages/B2B/PaymentCollection'
import AddPaymentCollection from 'pages/B2B/PaymentCollection/components/PaymentCollectionAdd'
import PaymentCollectionSearchForm from 'pages/B2B/PaymentCollection/components/PaymentCollectionSearchForm'
import PaymentSearch from 'pages/B2B/PaymentRecipt'
import AddEditPaymentReceipt from 'pages/B2B/PaymentRecipt/components/AddPayment'
import PaymentReceiptSearchForm from 'pages/B2B/PaymentRecipt/components/PaymentSearch'
import PendingPayment from 'pages/B2B/PendingPayment'
import PendingPaymentSearchForm from 'pages/B2B/PendingPayment/components/PendingPayment'
import TDPEdit from 'pages/B2B/TDP/Edit'
import TDPManagement from 'pages/B2B/TDP/Management'
import TDPRegistration from 'pages/B2B/TDP/Registration'
import TechnologyCompany from 'pages/B2B/TechnologyCompany'
import AddTechnologyCompany from 'pages/B2B/TechnologyCompany/components/AddTechnologyCompany'
import SearchTechnologyCompany from 'pages/B2B/TechnologyCompany/components/SearchTechnologyCompany'
import WalletAdjustment from 'pages/B2B/WalletAdjustment'
import AddStatement from 'pages/B2B/WalletAdjustment/components/AddWalletAdjustment'
import SearchWallet from 'pages/B2B/WalletAdjustment/components/SearchWallet'
import WalletManagement from 'pages/B2B/WalletStatement'
import WalletStatement from 'pages/B2B/WalletStatement/components/WalletStatement'
import CampaignView from 'pages/Campaign/CampaignView'
import ExternalPlatformDashboard from 'pages/ExternalPlatform/ExternalPlatformDashboard'
import SearchPayments from 'pages/Finance/SearchPayments'
import ExternalUsersDashboard from 'pages/OTPacks/Global/ExternalUsersDashboard'
import ExternalUserViewProfilePage from 'pages/OTPacks/Global/ExternalUserViewProfilePage'
import FaberCatalogueDashboardV2 from 'pages/ProductCatalogue/FaberCatalogueDashboardV2'
import VouchersDashboard from 'pages/Vouchers/VouchersDashboard'
import UploadWhitelist from 'pages/Whitelist/UploadWhitelist'
import ViewWhitelist from 'pages/Whitelist/ViewWhitelist'
import WhitelistManagementPage from 'pages/Whitelist/WhitelistManagementPage'

import Layout from './common/Layout/Nav'

function App() {
  OrbitSession.set({ default: 'cookie' })
  const { identity } = useIdentity()

  const token = OrbitSession.get('faberToken')
  const navigate = useNavigate()

  const { mutate: validateToken } = useMutation(AuthFaberApi.validateToken, {
    onError: () => {
      navigate('/login')
    }
  })

  useEffect(() => {
    if (token) {
      validateToken({ faberToken: token })
    }
  }, [token])

  return (
    <>
      <Routes>
        <Route path='*' element={<NotFound />} />

        <Route path='/'>
          {['opsowner', 'ops', 'opsviewer'].includes(identity?.role || '') && (
            <Route index element={<Navigate to='/hq' />} />
          )}

          <Route index element={<Navigate to='/login' />} />
          <Route path='login' element={<LogIn />} />
        </Route>

        <Route
          element={
            <PermissionedRoute
              allowedRoles={['ops', 'opsowner', 'opsviewer', 'external']}
            />
          }
        >
          {/* BASE ROUTES */}
          <Route path='/hq' element={<Layout />}>
            <Route index element={<Navigate to='manage-global-cx' />} />
            <Route path='profile' element={<Profile />} />

            {/* SUBSCRIPTION */}
            <Route path='manage-customers-faber'>
              <Route index element={<FaberCustomerManagement />} />
              <Route
                path='view-profile/:id'
                element={<FaberViewProfilePage />}
              />
            </Route>

            <Route
              path='finance-faber'
              element={<FaberFinanceMainDashboard />}
            />

            <Route path='search-payments' element={<SearchPayments />} />

            <Route path='manage-inventory-faber'>
              <Route index element={<ESimsManagementPage />} />
              <Route path='upload' element={<UploadESims />} />
              <Route path='fulfillment' element={<PreorderUpload />} />
            </Route>

            <Route path='manage-whitelist'>
              <Route index element={<WhitelistManagementPage />} />
              <Route path='upload' element={<UploadWhitelist />} />
              <Route
                path='view-whitelist/:id/:version'
                element={<ViewWhitelist />}
              />
            </Route>

            <Route path='manage-otpack'>
              <Route index element={<FaberOtpDashboard />} />
              <Route
                path='view-profile/:id'
                element={<FaberViewProfilePageV2 />}
              />
            </Route>

            <Route path='manage-global-cx'>
              <Route index element={<FaberGlobalDashboard />} />
              <Route
                path='view-profile/:id'
                element={<FaberViewProfilePageV2 />}
              />
            </Route>

            <Route path='manage-external-users'>
              <Route index element={<ExternalUsersDashboard />} />
              <Route
                path='view-external-profile/:id'
                element={<ExternalUserViewProfilePage />}
              />
            </Route>

            <Route path='manage-catalogue'>
              <Route index element={<FaberCatalogueDashboardV2 />} />
            </Route>

            <Route path='manage-campaign'>
              <Route index element={<CampaignView />} />
            </Route>

            <Route path='manage-rewards'>
              <Route index element={<FaberRewardsDashboard />} />
              <Route
                path='view-profile/:id'
                element={<FaberViewProfilePageV2 />}
              />
            </Route>

            <Route path='manage-vouchers'>
              <Route index element={<VouchersDashboard />} />
            </Route>

            <Route path='manage-external-platforms'>
              <Route index element={<ExternalPlatformDashboard />} />
            </Route>

            <Route path='manage-terminated-faber'>
              <Route
                index
                element={<FaberTerminatedCustomersMainDashboard />}
              />
            </Route>

            <Route path='b2b'>
              <Route index element={<TDPManagement />} />
              <Route path='tdp/registration' element={<TDPRegistration />} />
              <Route path='tdp/edit' element={<TDPEdit />} />
              <Route path='3ptp' element={<TechnologyCompany />}>
                <Route index element={<SearchTechnologyCompany />} />
                <Route path='add' element={<AddTechnologyCompany />} />
              </Route>
              <Route path='catalog' element={<CatalogManagement />}>
                <Route index element={<SearchCatalogGroup />} />
                <Route path='add' element={<AddCatalogGroup />} />
              </Route>
              <Route path='api-out-access' element={<APIOutAccessManagement />}>
                <Route index element={<SearchAPIKey />} />
                <Route path='add' element={<AddAPIKey />} />
              </Route>
              <Route path='order' element={<OrderManagement />}>
                <Route index element={<OrderSearchForm />} />
              </Route>
              <Route path='wallet-statement' element={<WalletManagement />}>
                <Route index element={<WalletStatement />} />
              </Route>
              <Route path='customer-statement' element={<CustomerSearch />}>
                <Route index element={<CustomerStatementSearchForm />} />
              </Route>
              <Route path='pending-payment' element={<PendingPayment />}>
                <Route index element={<PendingPaymentSearchForm />} />
              </Route>
              <Route path='wallet-adjustment' element={<WalletAdjustment />}>
                <Route index element={<SearchWallet />} />
                <Route path='register' element={<AddStatement />} />
              </Route>
              <Route path='payments' element={<PaymentSearch />}>
                <Route index element={<PaymentReceiptSearchForm />} />
                <Route path='add' element={<AddEditPaymentReceipt />} />
              </Route>
              <Route path='payment-collection' element={<PaymentCollection />}>
                <Route index element={<PaymentCollectionSearchForm />} />
                <Route path='add' element={<AddPaymentCollection />} />
              </Route>
            </Route>

            {/* ROOT ONLY ROUTES */}
            <Route element={<PermissionedRoute allowedRoles={['opsowner']} />}>
              <Route path='manage-accounts'>
                <Route index element={<AccountManagement />} />
                <Route path='add' element={<AddAccount />} />
                <Route path='view' element={<ViewAndUpdateAccount />} />
              </Route>
            </Route>
          </Route>
        </Route>
      </Routes>
    </>
  )
}

export default App
